import React from 'react';
import _ from 'underscore';
import { GIFTING_FAQ_CMS_CATEGORY_LABEL } from './giftingFaqConstants';

// Memoized markdown components factory - cache by className
const markdownComponentsCache = new Map();

export const createMarkdownComponents = (imageClassName) => {
  if (markdownComponentsCache.has(imageClassName)) {
    return markdownComponentsCache.get(imageClassName);
  }

  const components = {
    // eslint-disable-next-line react/prop-types, react/destructuring-assignment
    p: ({ children }) => {
      // If the paragraph only contains an image, return the image directly without p tag
      const childrenArray = Array.isArray(children) ? children : [children];
      if (childrenArray.length === 1) {
        const firstChild = childrenArray[0];
        if (React.isValidElement(firstChild) && firstChild.type === 'img') {
          return firstChild;
        }
      }
      return <p>{children}</p>;
    },
    // eslint-disable-next-line react/prop-types
    img: ({ node, alt, ...props }) => {
      return <img {...props} alt={alt || ''} className={imageClassName} />; // eslint-disable-line react/jsx-props-no-spreading
    }
  };

  markdownComponentsCache.set(imageClassName, components);
  return components;
};

// Helper function to find CMS node by key
export const findCmsNode = (cmsNode, key) => {
  if (!cmsNode || !cmsNode.children) return null;
  return _.find(cmsNode.children, { key });
};

export const normalizeFaqCategoryLabel = (value) => String(value ?? '').trim().toLowerCase();

/**
 * CMS returns requested keys as root nodes (see about_us_page in cms tests).
 * Contact FaqSection uses cmsData directly when categories are top-level;
 * when a single `faq_section` root wraps category children, unwrap for lookup.
 */
export const resolveFaqCategorySections = (cmsData) => {
  if (!Array.isArray(cmsData) || cmsData.length === 0) {
    return [];
  }
  if (cmsData.length === 1 && cmsData[0]?.key === 'faq_section') {
    return cmsData[0].children || [];
  }
  return cmsData;
};

export const findFaqCategoryByLabel = (sections, categoryLabel) => {
  if (!categoryLabel) {
    return null;
  }
  const categories = resolveFaqCategorySections(sections);
  if (!categories.length) {
    return null;
  }
  const targetLabel = normalizeFaqCategoryLabel(categoryLabel);
  return (
    categories.find(
      (section) => normalizeFaqCategoryLabel(section?.value) === targetLabel,
    ) || null
  );
};

export const mapFaqCategoryToQuestionItems = (categorySection) => {
  if (!categorySection?.children?.length) {
    return [];
  }

  return categorySection.children.map((faqQuestion) => {
    const answerNode = faqQuestion.children?.[0];
    return {
      cmsKeyId: faqQuestion.cmsKeyId,
      question: faqQuestion.value,
      answer: answerNode?.value ?? '',
      cmsNode: faqQuestion,
      answerCmsNode: answerNode,
    };
  });
};

export const getDeliveryFaqItems = (
  sections,
  categoryLabel = GIFTING_FAQ_CMS_CATEGORY_LABEL,
) => {
  const category = findFaqCategoryByLabel(sections, categoryLabel);
  return mapFaqCategoryToQuestionItems(category);
};

/** Delivery FAQ list + CMS category node for CmsInlineEdit (single tree walk). */
export const getDeliveryFaqCategoryPayload = (
  sections,
  categoryLabel = GIFTING_FAQ_CMS_CATEGORY_LABEL,
) => {
  const category = findFaqCategoryByLabel(sections, categoryLabel);
  return {
    category,
    items: mapFaqCategoryToQuestionItems(category),
  };
};

